import { Grid, InputLabel, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, Box, CircularProgress, Card, Tooltip } from "@mui/material";
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import FormField from "layouts/pages/users/new-user/components/FormField";
import { useEffect, useState } from "react";
import { Form, Formik, useFormikContext } from "formik";
import * as Yup from "yup";
import { useUser } from "context/UserContext";
import MDSelect from "components/MDSelect";
import DevedorEmailSection from "./DevedorEmailSection";
import DevedorTelefoneSection from "./DevedorTelefoneSection";
import DevedorEnderecoSection from "./DevedorEnderecoSection";
import { get, post, postNoResponse, postUploadNoResponse } from "utils/WebService";
import MDSnackbar from "components/MDSnackbar";
import { successAlert } from "utils/Functions";

const validationSchema = Yup.object().shape({
    nomeDevedor: Yup.string().required("Campo obrigatório"),
    codigoCliente: Yup.string().required("Campo obrigatório"),
    idPessoa: Yup.string().required("Selecione um cliente"),
    emails: Yup.array().of(
        Yup.object().shape({
            ccEmail: Yup.string().test('is-valid-email', 'E-mail inválido', value => {
                if (!value) return true;
                const strEmail = String(value).toLowerCase();
                const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                if (!re.test(strEmail)) return false;
                if (strEmail.endsWith("@gamil.com")) return false;
                if (strEmail.endsWith("@hotmai.com") || strEmail.endsWith("@hotamil.com") || strEmail.endsWith("@outlok.com")) return false;
                return true;
            })
        })
    )
});

export default function CadastroBasico({ formRef, recordId, isJuridico, closeCreateEdit, isViewOnly, pendingContratos }) {
    const { processoStatus, userPerfils, userFunctionalities } = useUser();
    const isAdminOrSuporte = userPerfils && userPerfils.some(p => {
        if (typeof p === 'number') return [1, 3, 4, 6].includes(p);
        const tipo = typeof p === 'object' ? (p?.tipoPerfil ?? p?.cvTipo ?? p?.cvCodPerfil ?? p?.id ?? p?.code) : null;
        if (tipo !== null && tipo !== undefined && [1, 3, 4, 6].includes(Number(tipo))) return true;
        const name = (typeof p === 'string' ? p : (p?.nome || p?.ccNome || p?.label || '')).toLowerCase();
        return name.includes("admin") || name.includes("gestor") || name.includes("coord") || name.includes("suporte");
    });
    
    const canCreate = userFunctionalities && userFunctionalities.includes("c050_C");
    const canEdit = userFunctionalities && userFunctionalities.includes("c050_U");
    const hasPermissionToEdit = recordId ? (canEdit || isAdminOrSuporte) : (canCreate || canEdit || isAdminOrSuporte);

    const disableFields = isViewOnly || !hasPermissionToEdit;
    const [codigoDiferente, setCodigoDiferente] = useState(false);
    const [usuarios, setUsuarios] = useState([]);
    const [statusFicha, setStatusFicha] = useState([]);
    const [clientes, setClientes] = useState([]);
    const [marcacoes, setMarcacoes] = useState([]);
    const [successSB, setSuccessSB] = useState(false);
    const [errorSB, setErrorSB] = useState(false);
    const [openDevolverModal, setOpenDevolverModal] = useState(false);
    const [devolverObs, setDevolverObs] = useState("");
    const [devolverFile, setDevolverFile] = useState(null);
    const [isSubmittingDevolucao, setIsSubmittingDevolucao] = useState(false);
    const [confirmAction, setConfirmAction] = useState({ open: false, title: "", message: "", onConfirm: null });

    useEffect(() => {
        get("/pessoa/list-all").then(({ list }) => setClientes(list || [])).catch(() => {});

        get(`/users/list-all-operators`)
            .then((res) => {
                setUsuarios(res.list || res.data || (Array.isArray(res) ? res : []));
            }).catch(() => {
                setUsuarios([]);
            });

        get("/status-ficha/list-all").then((res) => {
            let list = res.list || res.data || (Array.isArray(res) ? res : []);
            setStatusFicha(list.sort((a, b) => String(a.ccNome || a.nome || "").localeCompare(String(b.ccNome || b.nome || ""))));
        }).catch(() => {
            setStatusFicha([]);
        });

        get("/marcacoes").then((res) => {
            const list = res.list || res.data || (Array.isArray(res) ? res : []);
            setMarcacoes(list.filter(m => m.cvAlvo === 1 || m.cvAlvo === 3));
        }).catch(() => setMarcacoes([]));
    }, []);

    return (
        <MDBox pb={3}>
            <Formik
                innerRef={formRef}
                initialValues={{
                    id: recordId || null,
                    idPessoa: null,
                    nomeDevedor: '',
                    cpfCnpj: '',
                    codigoCliente: '',
                    idUsuario: null,
                    status: null,
                    emails: [],
                    enderecos: [],
                    telefones: [],
                    marcacoesIds: [],
                    observacao: '',
                    fichas: []
                }}
                validationSchema={validationSchema}
                onSubmit={(values, { setSubmitting, resetForm }) => {
                    const codFinal = (values.codigoCliente || '').trim() || (values.cpfCnpj || '').trim();
                    if (!codFinal) {
                        alert("O campo Código do Cliente não pode ser vazio!");
                        setSubmitting(false);
                        return;
                    }

                    const payload = {
                        id: values.id,
                        idPessoa: values.idPessoa,
                        nome: values.nomeDevedor,
                        documento: values.cpfCnpj,
                        codigoCliente: codFinal,
                        idUsuario: values.idUsuario,
                        idStatusFicha: values.status,
                        emails: values.emails,
                        enderecos: values.enderecos,
                        telefones: values.telefones,
                        marcacoesIds: values.marcacoesIds,
                        contratos: pendingContratos || [],
                        observacao: values.observacao
                    };
                    
                    if (!values.id && (!pendingContratos || pendingContratos.length === 0)) {
                        alert("Não é possível salvar o devedor sem cadastrar um débito/contrato. Vá até a aba Contratos e adicione ao menos um.");
                        setSubmitting(false);
                        return;
                    }

                    setErrorSB(false);
                    setSuccessSB(false);

                    return postNoResponse("/devedor/save", payload)
                        .then(() => {
                            setSuccessSB(true);
                            setSubmitting(false);
                            resetForm({ values });
                            if (!values.id && closeCreateEdit) {
                                successAlert("Ficha do devedor salva com sucesso!", closeCreateEdit);
                            }
                        })
                        .catch((error) => {
                            console.error("Erro ao salvar devedor", error);
                            setErrorSB(true);
                            setSubmitting(false);
                        });
                }}
            >
                {({ values, initialValues, handleChange, setFieldValue, errors, touched, isSubmitting, dirty }) => {
                    const isEmailsDirty = JSON.stringify(values.emails) !== JSON.stringify(initialValues.emails);
                    const isTelefonesDirty = JSON.stringify(values.telefones) !== JSON.stringify(initialValues.telefones);
                    const isEnderecosDirty = JSON.stringify(values.enderecos) !== JSON.stringify(initialValues.enderecos);
                    return (
                    <Form>
                        {/* Dummy inputs to intercept Chrome autofill */}
                        <input type="text" name="dummy_username" style={{ display: 'none' }} aria-hidden="true" autoComplete="username" />
                        <input type="password" name="dummy_password" style={{ display: 'none' }} aria-hidden="true" autoComplete="current-password" />
                        <FetchAndSetData recordId={recordId} usuarios={usuarios} setUsuarios={setUsuarios} setCodigoDiferente={setCodigoDiferente} />
                        <Grid container spacing={3}>
                            <Grid item xs={12} sm={3}>
                                <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Cliente</InputLabel>
                                <MDSelect
                                    isMulti={false}
                                    getOptionValue={(option) => option.id}
                                    getOptionLabel={(option) => option.nomeComum}
                                    name="idPessoa"
                                    placeholder="Selecione..."
                                    value={clientes.find(c => c.id === values.idPessoa) || null}
                                    options={clientes}
                                    onChange={(value) => {
                                        setFieldValue("idPessoa", value ? value.id : null);
                                    }}
                                    isDisabled={disableFields}
                                />
                                {errors.idPessoa && touched.idPessoa && (
                                    <MDTypography variant="caption" color="error">{errors.idPessoa}</MDTypography>
                                )}
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <FormField
                                    type="text"
                                    label="Nome"
                                    name="nomeDevedor"
                                    value={values.nomeDevedor}
                                    onChange={handleChange}
                                    onBlur={(e) => {
                                        if (e.target.value) {
                                            post("/devedor/list", { page: 0, pageSize: 1, ccNome: e.target.value }).then(res => {
                                                if (res.data && res.data.length > 0) {
                                                    if (res.data.some(d => d.id !== recordId)) alert("Já existe um devedor com este Nome na base!");
                                                }
                                            }).catch(() => {});
                                        }
                                    }}
                                    placeholder="João da Silva"
                                    error={errors.nomeDevedor && touched.nomeDevedor}
                                    InputProps={{ readOnly: disableFields }}
                                    autoComplete="off"
                                    style={{ "& .MuiInputBase-root": { height: "40px", minHeight: "40px" } }}
                                />
                                {errors.nomeDevedor && touched.nomeDevedor && (
                                    <MDTypography variant="caption" color="error">{errors.nomeDevedor}</MDTypography>
                                )}
                            </Grid>
                            <Grid item xs={12} sm={2.5}>
                                <FormField
                                    type="text"
                                    label="CPF/CNPJ"
                                    name="cpfCnpj"
                                    value={values.cpfCnpj}
                                    onChange={(e) => {
                                        handleChange(e);
                                        if (!codigoDiferente) {
                                            setFieldValue("codigoCliente", e.target.value);
                                        }
                                    }}
                                    onBlur={(e) => {
                                        if (e.target.value) {
                                            post("/devedor/list", { page: 0, pageSize: 1, ccDocumento: e.target.value }).then(res => {
                                                if (res.data && res.data.length > 0) {
                                                    if (res.data.some(d => d.id !== recordId)) alert("Já existe um devedor com este CPF/CNPJ na base!");
                                                }
                                            }).catch(() => {});
                                        }
                                    }}
                                    placeholder="Informe CPF ou CNPJ"
                                    InputProps={{ readOnly: disableFields }}
                                    style={{ "& .MuiInputBase-root": { height: "40px", minHeight: "40px" } }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={2.5}>
                                <MDBox position="relative">
                                    <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Cód. Cliente *</InputLabel>
                                    {!disableFields && (
                                        <Tooltip title={codigoDiferente ? "Voltar a copiar automaticamente o CPF/CNPJ" : "Clique para digitar um Código diferente do Documento"}>
                                            <Box
                                                component="span"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    e.preventDefault();
                                                    const next = !codigoDiferente;
                                                    setCodigoDiferente(next);
                                                    if (!next) {
                                                        setFieldValue("codigoCliente", values.cpfCnpj || "");
                                                    }
                                                }}
                                                sx={{
                                                    position: "absolute",
                                                    top: "2px",
                                                    right: "8px",
                                                    zIndex: 2,
                                                    cursor: "pointer",
                                                    fontSize: "0.65rem",
                                                    lineHeight: 1,
                                                    py: "2px",
                                                    px: "6px",
                                                    borderRadius: "4px",
                                                    border: "1px solid",
                                                    borderColor: codigoDiferente ? "#fb8c00" : "#00b0ff",
                                                    backgroundColor: codigoDiferente ? "#fb8c00" : "transparent",
                                                    color: codigoDiferente ? "#fff !important" : "#00b0ff !important",
                                                    fontWeight: "bold",
                                                    userSelect: "none",
                                                    transition: "all 150ms ease-in-out",
                                                    "&:hover": {
                                                        opacity: 0.85
                                                    }
                                                }}
                                            >
                                                {codigoDiferente ? "Diferente" : "Diferente?"}
                                            </Box>
                                        </Tooltip>
                                    )}
                                    <MDInput
                                        name="codigoCliente"
                                        value={values.codigoCliente}
                                        onChange={handleChange}
                                        onBlur={(e) => {
                                            if (e.target.value) {
                                                post("/devedor/list", { page: 0, pageSize: 1, ccCodigoCliente: e.target.value }).then(res => {
                                                    if (res.data && res.data.length > 0) {
                                                        if (res.data.some(d => d.id !== recordId)) alert("Já existe um devedor com este Código na base!");
                                                    }
                                                }).catch(() => {});
                                            }
                                        }}
                                        placeholder={codigoDiferente ? "Informe código único" : (values.cpfCnpj || "Espelho CPF/CNPJ")}
                                        error={Boolean(errors.codigoCliente && touched.codigoCliente)}
                                        size="small"
                                        fullWidth
                                        InputProps={{
                                            readOnly: disableFields || !codigoDiferente,
                                            sx: !codigoDiferente ? { cursor: "not-allowed" } : {}
                                        }}
                                        sx={{ "& .MuiInputBase-root": { height: "40px", minHeight: "40px" } }}
                                    />
                                    {errors.codigoCliente && touched.codigoCliente && (
                                        <MDBox mt={0.5}>
                                            <MDTypography variant="caption" color="error">{errors.codigoCliente}</MDTypography>
                                        </MDBox>
                                    )}
                                </MDBox>
                            </Grid>
                            <Grid item xs={12} sm={3}>
                                <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Propriedade</InputLabel>
                                <MDSelect
                                    isMulti={false}
                                    getOptionValue={(option) => option.id}
                                    getOptionLabel={(option) => option.nome}
                                    name="idUsuario"
                                    placeholder="Selecione..."
                                    value={usuarios.find(u => u.id === values.idUsuario) || null}
                                    options={usuarios}
                                    onChange={(value) => {
                                        setFieldValue("idUsuario", value ? value.id : null);
                                    }}
                                    isDisabled={recordId ? disableFields : isViewOnly}
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Status</InputLabel>
                                <MDSelect
                                    isMulti={false}
                                    getOptionValue={(option) => option.id}
                                    getOptionLabel={(option) => option.ccNome}
                                    name="status"
                                    placeholder="Selecione..."
                                    value={statusFicha.find(s => s.id === values.status) || null}
                                    options={statusFicha}
                                    onChange={(value) => {
                                        setFieldValue("status", value ? value.id : null);
                                        if (isViewOnly) {
                                            const payload = {
                                                id: values.id,
                                                idPessoa: values.idPessoa,
                                                nome: values.nome,
                                                documento: values.cpfCnpj,
                                                codigoCliente: values.codigoCliente,
                                                idUsuario: values.idUsuario,
                                                idStatusFicha: value ? value.id : null,
                                                emails: values.emails,
                                                enderecos: values.enderecos,
                                                telefones: values.telefones,
                                                marcacoesIds: values.marcacoesIds,
                                                contratos: pendingContratos || [],
                                                observacao: values.observacao
                                            };
                                            postNoResponse("/devedor/save", payload)
                                                .then(() => setSuccessSB(true))
                                                .catch(() => setErrorSB(true));
                                        }
                                    }}
                                    isDisabled={false}
                                />
                            </Grid>
                            
                            <Grid item xs={12} sm={5}>
                                <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Marcação Especial</InputLabel>
                                <MDSelect
                                    isMulti={true}
                                    getOptionValue={(option) => option.cvId}
                                    getOptionLabel={(option) => option.ccNome}
                                    name="marcacoesIds"
                                    placeholder="Selecione as marcações..."
                                    value={marcacoes.filter(m => values.marcacoesIds && values.marcacoesIds.includes(m.cvId))}
                                    options={marcacoes}
                                    onChange={(selected) => {
                                        const val = selected ? selected.map(s => s.cvId) : [];
                                        setFieldValue("marcacoesIds", val);
                                    }}
                                    isDisabled={recordId ? disableFields : isViewOnly}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <FormField
                                    type="text"
                                    label="Observação"
                                    name="observacao"
                                    value={values.observacao}
                                    onChange={handleChange}
                                    placeholder="Digite suas observações..."
                                    multiline
                                    rows={3}
                                    InputProps={{
                                        readOnly: disableFields,
                                        sx: disableFields ? { backgroundColor: "#f0f2f5", cursor: "default" } : {}
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <MDBox>
                                    <DevedorEmailSection values={values} setFieldValue={setFieldValue} handleChange={handleChange} errors={errors} touched={touched} isViewOnly={false} />
                                </MDBox>
                            </Grid>

                            <Grid item xs={12}>
                                <MDBox mt={3}>
                                    <DevedorTelefoneSection values={values} setFieldValue={setFieldValue} handleChange={handleChange} isViewOnly={false} />
                                </MDBox>
                            </Grid>

                            <Grid item xs={12}>
                                <MDBox mt={3}>
                                    <DevedorEnderecoSection values={values} setFieldValue={setFieldValue} handleChange={handleChange} isViewOnly={false} />
                                </MDBox>
                            </Grid>
                            
                            <Grid item xs={12} display="flex" justifyContent="flex-end" mt={2} gap={2}>
                                {recordId && (
                                    isAdminOrSuporte ? (
                                        <MDButton variant="gradient" color="error" onClick={() => {
                                            setConfirmAction({
                                                open: true,
                                                title: "Devolver Ficha",
                                                message: "Deseja realmente devolver esta ficha?",
                                                onConfirm: async () => {
                                                    try {
                                                        await postNoResponse(`/devedor/devolver/${recordId}`, {});
                                                        setSuccessSB(true);
                                                        successAlert("Ficha devolvida com sucesso!", closeCreateEdit);
                                                    } catch (e) {
                                                        setErrorSB(true);
                                                    }
                                                }
                                            });
                                        }} disabled={isSubmitting || values.status === 1}>
                                            Devolver Ficha
                                        </MDButton>
                                    ) : (
                                        <MDButton variant="gradient" color="warning" onClick={() => setOpenDevolverModal(true)} disabled={isSubmitting || values.status === 1}>
                                            Solicitar Devolução
                                        </MDButton>
                                    )
                                )}
                                {recordId && values.status === 1 && isAdminOrSuporte && (
                                    <MDButton variant="gradient" color="success" onClick={() => {
                                        setConfirmAction({
                                            open: true,
                                            title: "Reativar Ficha",
                                            message: "Deseja realmente reativar esta ficha?",
                                            onConfirm: async () => {
                                                try {
                                                    await postNoResponse(`/devedor/reativar/${recordId}`, {});
                                                    setSuccessSB(true);
                                                    successAlert("Ficha reativada com sucesso!", closeCreateEdit);
                                                } catch (e) {
                                                    setErrorSB(true);
                                                }
                                            }
                                        });
                                    }} disabled={isSubmitting}>
                                        Reativar Ficha
                                    </MDButton>
                                )}
                                <MDButton variant="gradient" color="info" type="submit" disabled={!dirty || isSubmitting}>
                                    Salvar Cadastro
                                </MDButton>
                            </Grid>
                        </Grid>
                    </Form>
                    );
                }}
            </Formik>
            <MDSnackbar
                color="success"
                icon="check"
                title="Sucesso"
                content="Ficha do devedor salva com sucesso!"
                dateTime=""
                open={successSB}
                onClose={() => setSuccessSB(false)}
                close={() => setSuccessSB(false)}
                bgWhite
            />
            <MDSnackbar
                color="error"
                icon="warning"
                title="Erro"
                content="Ocorreu um erro ao salvar o devedor."
                dateTime=""
                open={errorSB}
                onClose={() => setErrorSB(false)}
                close={() => setErrorSB(false)}
                bgWhite
            />

            <Dialog open={openDevolverModal} onClose={() => !isSubmittingDevolucao && setOpenDevolverModal(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Solicitar Devolução de Ficha</DialogTitle>
                <DialogContent>
                    <Box mt={2}>
                        <TextField
                            label="Motivo / Observação"
                            multiline
                            rows={4}
                            fullWidth
                            value={devolverObs}
                            onChange={(e) => setDevolverObs(e.target.value)}
                            disabled={isSubmittingDevolucao}
                            required
                        />
                    </Box>
                    <Box mt={2}>
                        <MDTypography variant="caption" fontWeight="bold">Anexar Arquivo (Opcional)</MDTypography>
                        <input
                            type="file"
                            onChange={(e) => setDevolverFile(e.target.files[0])}
                            disabled={isSubmittingDevolucao}
                            style={{ display: "block", marginTop: "8px" }}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDevolverModal(false)} disabled={isSubmittingDevolucao}>Cancelar</Button>
                    <Button 
                        onClick={async () => {
                            if (!devolverObs) {
                                alert("Preencha o motivo/observação.");
                                return;
                            }
                            setIsSubmittingDevolucao(true);
                            try {
                                await postNoResponse(`/devedor/solicitar-devolucao/${recordId}`, { observacao: devolverObs });
                                if (devolverFile) {
                                    const formData = new FormData();
                                    formData.append("file", devolverFile);
                                    formData.append("devedorId", recordId);
                                    await postUploadNoResponse(`/documents/devedor/upload`, formData);
                                }
                                setOpenDevolverModal(false);
                                setSuccessSB(true);
                                setIsSubmittingDevolucao(false);
                                successAlert("Solicitação de devolução enviada com sucesso!", closeCreateEdit);
                            } catch (e) {
                                setErrorSB(true);
                                setIsSubmittingDevolucao(false);
                            }
                        }} 
                        variant="contained" 
                        color="primary" 
                        disabled={isSubmittingDevolucao}
                        sx={{ color: "#fff" }}
                    >
                        {isSubmittingDevolucao ? <CircularProgress size={24} color="inherit" /> : "Enviar Solicitação"}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={confirmAction.open} onClose={() => setConfirmAction({ ...confirmAction, open: false })}>
                <DialogTitle>{confirmAction.title}</DialogTitle>
                <DialogContent>
                    <MDTypography variant="body2" mt={1}>
                        {confirmAction.message}
                    </MDTypography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmAction({ ...confirmAction, open: false })}>Cancelar</Button>
                    <Button 
                        variant="contained" 
                        color="primary" 
                        sx={{ color: "#fff" }}
                        onClick={async () => {
                            const action = confirmAction.onConfirm;
                            setConfirmAction({ ...confirmAction, open: false });
                            if (action) await action();
                        }}
                    >
                        Confirmar
                    </Button>
                </DialogActions>
            </Dialog>
        </MDBox>
    );
}

const FetchAndSetData = ({ recordId, usuarios, setUsuarios, setCodigoDiferente }) => {
    const { resetForm } = useFormikContext();

    useEffect(() => {
        if (recordId) {
            get(`/devedor/by-id/${recordId}`).then(data => {
                if (data.idUsuario && !usuarios.find(u => u.id === data.idUsuario)) {
                    setUsuarios(prev => {
                        if (!prev.find(u => u.id === data.idUsuario)) {
                            return [...prev, { id: data.idUsuario, nome: data.nomeUsuario || "Operador Sem Acesso / Inativo" }];
                        }
                        return prev;
                    });
                }
                const doc = data.documento || '';
                const cod = data.codigoCliente || '';
                if (setCodigoDiferente) {
                    setCodigoDiferente(Boolean(cod && doc && cod.trim() !== doc.trim()));
                }
                resetForm({
                    values: {
                        id: data.id,
                        idPessoa: data.idPessoa,
                        nomeDevedor: data.nome || '',
                        cpfCnpj: doc,
                        codigoCliente: cod,
                        idUsuario: data.idUsuario,
                        status: data.idStatusFicha,
                        emails: data.emails || [],
                        enderecos: data.enderecos || [],
                        telefones: data.telefones || [],
                        marcacoesIds: data.marcacoesIds || [],
                        observacao: data.observacao || '',
                        fichas: data.fichas || []
                    }
                });
            }).catch(console.error);
        }
    }, [recordId, resetForm, usuarios, setUsuarios, setCodigoDiferente]);

    return null;
};
